import { Button, Menu, stopPropagation, Text } from "@overmap-ai/blocks"
import {
	type Asset,
	type Issue,
	type IssueTypeIdentifier,
	type IssueTypePriority,
	issueTypePrioritySortFn,
	type IssueTypeStatus,
	type Stored,
	toUuidIdRecord,
	UNTITLED_ISSUE_TITLE,
} from "@overmap-ai/common"
import { selectAssetsByIds, selectUsers } from "@overmap-ai/core"
import { FieldSchema } from "@overmap-ai/forms"
import { useNavigate } from "@tanstack/react-router"
import {
	type ColumnDef,
	getCoreRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table"
import { useCallback, useMemo } from "react"

import { useIssue } from "../../../hooks/issues"
import { useAssignees } from "../../../hooks/users"
import { useAppSelector } from "../../../store"
import type { IssueData } from "../../../typings"
import { formatCompactDate, formatTimestampDate } from "../../../utils/date"
import { getUserDisplayLabel } from "../../../utils/users"
import { AssigneeSelect } from "../../DropdownSelects/AssigneeSelect"
import { IssuePrioritySelect } from "../../DropdownSelects/IssuePrioritySelect"
import { IssueTypeStatusSelect } from "../../DropdownSelects/IssueTypeStatusSelect"
import { AssetIcon } from "../../Icons/AssetIcon"
import { IssuePriorityIcon } from "../../Icons/IssuePriorityIcon"
import { IssueTypeStatusIcon } from "../../Icons/IssueTypeStatusIcon"
import { UserIcon } from "../../Icons/UserIcon"
import { IssueIndexLabel } from "../../IndexLabels/IssueIndex"
import { AssetLabel } from "../../Labels"
import { AssetLink } from "../../Links"
import { AssetGroup } from "../../MenuGroups/AssetGroup"
import { DayPickerPopover } from "../../Popovers/DayPickerPopover"
import { TextInputPopover } from "../../Popovers/TextInputPopover"
import { ColumnSortButton, ColumnSortIcon, RowSelectAllCheckbox, RowSelectCheckbox } from "../components"
import { getColumnDefsOfFieldSchema, getColumnDefsOfIdentifiers } from "../utils"

interface IssueTableColumnsConfig {
	issueTypeIdentifiers: Stored<IssueTypeIdentifier>[]
	issueTypePriorities: Stored<IssueTypePriority>[]
	issueTypeStatuses: Stored<IssueTypeStatus>[]
	issueTypeFieldSchema: FieldSchema
}

export const useIssueTableColumns = (config: IssueTableColumnsConfig) => {
	const { issueTypePriorities, issueTypeStatuses, issueTypeFieldSchema, issueTypeIdentifiers } = config

	const issueTypePrioritiesMapping = useMemo(() => {
		return toUuidIdRecord(issueTypePriorities)
	}, [issueTypePriorities])

	return useMemo<ColumnDef<IssueData>[]>(
		() => [
			{
				id: "select",
				enableHiding: false,
				header({ table }) {
					return (
						<RowSelectAllCheckbox
							toggleAllRowsSelected={table.toggleAllRowsSelected}
							isSomeRowsSelected={table.getIsSomeRowsSelected()}
							isAllRowsSelected={table.getIsAllRowsSelected()}
						/>
					)
				},
				cell({ row }) {
					return (
						<RowSelectCheckbox isRowSelected={row.getIsSelected()} toggleRowSelected={row.toggleSelected} />
					)
				},
			},
			{
				id: "created_by",
				accessorFn: (originalRow) => originalRow.issue.created_by,
				meta: {
					plainHeader: "Creator",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Creator
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ getValue }) {
					const userId = getValue<Issue["created_by"]>()

					return <UserIcon size="sm" userId={userId ?? undefined} />
				},
			},
			{
				id: "index",
				accessorFn: (originalRow) => originalRow.issue.index,
				meta: {
					plainHeader: "Index",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Index
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ row }) {
					const issue = row.original.issue
					return <IssueIndexLabel highContrast issueId={issue.uuid} />
				},
			},
			{
				id: "title",
				accessorFn: (originalRow) => originalRow.issue.title || undefined,
				sortUndefined: -1,
				meta: {
					plainHeader: "Title",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Title
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ getValue, row }) {
					const issue = row.original.issue
					const value = getValue<Issue["title"]>()

					const { handleTitleChange } = useIssue(issue)

					return (
						<TextInputPopover
							placeholder="Enter a title"
							onValueChange={handleTitleChange}
							value={value ?? undefined}
							size="sm"
						>
							<Button variant="ghost" size="sm" accentColor="base" onClick={stopPropagation}>
								<Text accentColor="base" highContrast={!!value}>
									{getValue<string>() || UNTITLED_ISSUE_TITLE}
								</Text>
							</Button>
						</TextInputPopover>
					)
				},
			},
			{
				id: "submitted_at",
				sortingFn: "datetime",
				accessorFn: (originalRow) => originalRow.issue.submitted_at,
				meta: {
					plainHeader: "Submitted at",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Submitted at
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					return <Text accentColor="base">{formatTimestampDate(getValue<string>())}</Text>
				},
			},
			{
				id: "linked_to",
				accessorFn: (row) => row.issueAssociations.length,
				meta: {
					plainHeader: "Linked to",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Linked to
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ row }) {
					const issueAssociations = row.original.issueAssociations
					const navigate = useNavigate()

					const assets = useAppSelector(
						selectAssetsByIds(issueAssociations.map((association) => association.asset!)),
					)

					const handleSelectAsset = useCallback(
						(asset: Stored<Asset>) => {
							void navigate({
								from: "/$organizationId/$projectId",
								to: "/$organizationId/$projectId/assets/$assetId",
								params: (prev) => ({
									...prev,
									assetId: asset.uuid,
								}),
							})
						},
						[navigate],
					)

					const getRender = () => {
						switch (issueAssociations.length) {
							case 0:
								return <Text accentColor="base">None</Text>
							case 1: {
								const assetId = issueAssociations[0].asset!

								return (
									<AssetLink assetId={assetId} onClick={stopPropagation}>
										<Button size="sm" accentColor="base" variant="ghost">
											<AssetIcon assetId={assetId} background={false} />
											<AssetLabel assetId={assetId} />
										</Button>
									</AssetLink>
								)
							}
							default:
								return (
									<Menu.Root>
										<Menu.ClickTrigger>
											<Button
												size="sm"
												accentColor="base"
												variant="ghost"
												onClick={stopPropagation}
											>
												{issueAssociations.length} linked
											</Button>
										</Menu.ClickTrigger>
										<Menu.Content size="sm">
											<Menu.Scroll>
												<AssetGroup assets={assets} onSelectAsset={handleSelectAsset} />
											</Menu.Scroll>
										</Menu.Content>
									</Menu.Root>
								)
						}
					}

					return getRender()
				},
			},
			{
				id: "status",
				accessorFn: (originalRow) => originalRow.issue.status,
				meta: {
					plainHeader: "Status",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Status
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ getValue, row }) {
					const issue = row.original.issue
					const status = getValue<IssueTypeStatus["uuid"]>()

					const { issueTypeStatus, handleStatusChange } = useIssue(issue)

					return (
						<IssueTypeStatusSelect
							required
							issueTypeStatuses={issueTypeStatuses}
							value={issueTypeStatus}
							onValueChange={handleStatusChange}
							modal
							size="sm"
						>
							<Button size="sm" accentColor="base" variant="ghost" onClick={stopPropagation}>
								<IssueTypeStatusIcon issueTypeStatusId={status} />
								{issueTypeStatus?.name}
							</Button>
						</IssueTypeStatusSelect>
					)
				},
			},
			{
				id: "priority",
				accessorFn: (originalRow) => originalRow.issue.priority,
				meta: {
					plainHeader: "Priority",
				},
				sortingFn: (rowA, rowB, _columnId) => {
					const issuePriorityA = issueTypePrioritiesMapping[rowA.original.issue.priority]
					const issuePriorityB = issueTypePrioritiesMapping[rowB.original.issue.priority]

					return issueTypePrioritySortFn(issuePriorityA, issuePriorityB)
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Priority
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ getValue, row }) {
					const issue = row.original.issue
					const priority = getValue<IssueTypePriority["uuid"]>()

					const { issueTypePriority, handlePriorityChange } = useIssue(issue)

					return (
						<IssuePrioritySelect
							required
							priorities={issueTypePriorities}
							value={issueTypePriority}
							onValueChange={handlePriorityChange}
							modal
							size="sm"
						>
							<Button size="sm" accentColor="base" variant="ghost" onClick={stopPropagation}>
								<IssuePriorityIcon priority={priority} />
								{issueTypePriority.name}
							</Button>
						</IssuePrioritySelect>
					)
				},
			},
			{
				id: "assigned_to",
				accessorFn: (originalRow) => originalRow.issue.assigned_to,
				meta: {
					plainHeader: "Assigned to",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Assigned to
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ row }) {
					const issue = row.original.issue

					// TODO: Should be accepted through props
					const allUsers = useAssignees(useAppSelector(selectUsers))
					const { assignee, handleAssigneeChange } = useIssue(issue)

					return (
						<AssigneeSelect
							required
							value={assignee}
							onValueChange={handleAssigneeChange}
							users={allUsers}
							modal
							size="sm"
						>
							<Button size="sm" accentColor="base" variant="ghost" onClick={stopPropagation}>
								{assignee ? (
									<>
										<UserIcon size="xs" userId={assignee.id} />
										<Text className="truncate">{getUserDisplayLabel(assignee)}</Text>
									</>
								) : (
									"Unassigned"
								)}
							</Button>
						</AssigneeSelect>
					)
				},
			},
			{
				id: "due_date",
				accessorFn: (originalRow) => originalRow.issue.due_date ?? undefined,
				sortingFn: "datetime",
				sortUndefined: -1,
				meta: {
					plainHeader: "Due date",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Due date
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ getValue, row }) {
					const issue = row.original.issue
					const value = getValue<Issue["due_date"]>()

					const { handleDueDateChange } = useIssue(issue)

					return (
						<DayPickerPopover
							mode="single"
							size="sm"
							required={false}
							selected={issue?.due_date ? new Date(issue.due_date) : undefined}
							onSelect={handleDueDateChange}
							modal
						>
							<Button size="sm" accentColor="base" variant="ghost" onClick={stopPropagation}>
								{value ? formatCompactDate(getValue<string>()) : "None"}
							</Button>
						</DayPickerPopover>
					)
				},
			},
			...getColumnDefsOfIdentifiers<Stored<IssueTypeIdentifier>, IssueData>(
				issueTypeIdentifiers,
				(identifier) => (data) => data.issueTypeIdentifierValues[identifier.uuid]?.value,
			),
			...getColumnDefsOfFieldSchema<IssueData>(issueTypeFieldSchema, (data) =>
				issueTypeFieldSchema.deserializeValues(data.issueTypeFieldValues?.values ?? {}),
			),
		],
		[
			issueTypePrioritiesMapping,
			issueTypeFieldSchema,
			issueTypeIdentifiers,
			issueTypePriorities,
			issueTypeStatuses,
		],
	)
}

export const useIssueTable = (args: Omit<TableOptions<IssueData>, "getCoreRowModel">) => {
	return useReactTable<IssueData>({
		...args,
		getRowId: (originalRow) => originalRow.issue.uuid,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
	})
}
